import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  AlertTriangle,
  Check,
  ChevronLeft,
  ChevronRight,
  Clock,
  Film,
  GripVertical,
  Loader2,
  Pause,
  Play,
  RotateCcw,
  Scissors,
  Sparkles,
  Trash2,
  Undo2,
} from 'lucide-react';
import { quoteImagemotionUsage } from '@rehub/pricing';
import { quoteImagemotionGeneration } from '../services/supabase';
import {
  REWORK_EXAMPLES,
  acceptRework,
  activeVersion,
  attachPendingRework,
  formatTimecode,
  includedClips,
  mapReworkInstructions,
  moveClip,
  parseTimeInput,
  previewNeedsApproximation,
  reconstructEditState,
  rejectRework,
  renderCreditCopy,
  reorderClips,
  resetClipTrim,
  restoreVersion,
  reworkCreditCopy,
  selectedDuration,
  setClipIncluded,
  setClipTrim,
  totalEditedDuration,
  validateEditState,
  validateTrim,
  type ClipEdit,
  type ProjectEditState,
} from '../lib/video-edit';
import {
  acceptPendingClip,
  discardPendingClip,
  invokeGenerateMotion,
  invokeRender,
  saveProjectEdit,
} from '../services/supabase';
import type { ImagemotionProject, ImagemotionProjectImage } from '../types';

type AutosaveStatus = 'saved' | 'saving' | 'error' | 'idle';

interface VideoEditorProps {
  project: ImagemotionProject;
  images: ImagemotionProjectImage[];
  onReload: () => Promise<void> | void;
  onOpenBuyCredits?: () => void;
}

function mergePendingFromImages(state: ProjectEditState, images: ImagemotionProjectImage[]): ProjectEditState {
  return {
    ...state,
    clips: state.clips.map((clip) => {
      const image = images.find((row) => row.id === clip.id);
      if (!image?.pending_clip_url) return clip;
      if (clip.pendingRework?.clipUrl === image.pending_clip_url) return clip;
      return {
        ...clip,
        pendingRework: {
          generationId: image.pending_generation_id ?? clip.pendingRework?.generationId ?? null,
          instructions: clip.pendingRework?.instructions || '',
          startedAt: clip.pendingRework?.startedAt || new Date().toISOString(),
          status: image.pending_generation_id && !image.pending_clip_url ? 'processing' : 'completed',
          clipUrl: image.pending_clip_url,
          clipPath: image.pending_clip_path ?? null,
          duration: image.pending_clip_duration ?? clip.sourceDuration,
        },
      };
    }),
  };
}

function firstClipNeedingDecision(clips: ClipEdit[]): ClipEdit | undefined {
  return clips.find((clip) => Boolean(clip.pendingRework?.clipUrl))
    ?? clips.find((clip) => Boolean(clip.pendingRework) && clip.pendingRework?.status !== 'failed');
}

function clipCardBorder(opts: { included: boolean; selected: boolean; emphasis?: boolean; dragging?: boolean }) {
  if (!opts.included) return 'border-slate-700 opacity-50';
  if (opts.emphasis) return 'border-indigo-400 ring-2 ring-indigo-400/40';
  if (opts.selected) return 'border-blue-500 ring-2 ring-blue-500/30';
  return 'border-slate-700';
}

function SortableClipCard({
  clip,
  selected,
  index,
  paired,
  onSelect,
}: {
  clip: ClipEdit;
  selected: boolean;
  index: number;
  paired: boolean;
  onSelect: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: clip.id });
  const version = activeVersion(clip);
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`relative w-36 shrink-0 rounded-lg border overflow-hidden ${clipCardBorder({
        included: clip.included,
        selected,
        dragging: isDragging,
      })} ${isDragging ? 'z-20 shadow-xl' : ''}`}
    >
      <button type="button" onClick={onSelect} className="block w-full text-left">
        <div className="aspect-video bg-slate-950">
          {version?.clipUrl ? (
            <video src={version.clipUrl} muted playsInline className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-[10px] text-slate-500">No clip</div>
          )}
        </div>
        <div className="px-2 py-1.5 bg-slate-800/90">
          <p className="text-[11px] text-white font-medium truncate">
            Clip {index + 1}{paired ? ' · Original' : ''}
          </p>
          <p className="text-[10px] text-slate-400">
            {formatTimecode(selectedDuration(clip))}
            {!clip.included && ' · Excluded'}
          </p>
        </div>
      </button>
      <button
        type="button"
        className="absolute top-1 right-1 p-1 rounded bg-black/50 text-slate-300 hover:text-white"
        aria-label="Drag to reorder"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

function ChoiceClipCard({
  clip,
  selected,
  index,
  onSelect,
}: {
  clip: ClipEdit;
  selected: boolean;
  index: number;
  onSelect: () => void;
}) {
  const replacementUrl = clip.pendingRework?.clipUrl;
  const waiting = !replacementUrl;
  return (
    <div
      className={`relative w-36 shrink-0 rounded-lg border overflow-hidden ${clipCardBorder({
        included: clip.included,
        selected,
        emphasis: Boolean(replacementUrl),
      })}`}
    >
      <button type="button" onClick={waiting ? undefined : onSelect} className="block w-full text-left" disabled={waiting}>
        <div className="aspect-video bg-slate-950 relative">
          {replacementUrl ? (
            <video src={replacementUrl} muted playsInline className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Loader2 className="w-4 h-4 animate-spin text-indigo-300" />
            </div>
          )}
          <span className="absolute bottom-1 left-1 px-1.5 py-0.5 rounded bg-indigo-600 text-[10px] font-semibold text-white">
            {waiting ? 'Working' : 'New'}
          </span>
        </div>
        <div className="px-2 py-1.5 bg-slate-800/90">
          <p className="text-[11px] text-white font-medium truncate">Clip {index + 1} · New</p>
          <p className="text-[10px] text-indigo-200">
            {waiting ? 'Generating…' : 'Click to watch'}
          </p>
        </div>
      </button>
    </div>
  );
}

export default function VideoEditor({ project, images, onReload, onOpenBuyCredits }: VideoEditorProps) {
  const reconstructed = useMemo(
    () => reconstructEditState(images, project.edit_state, {
      hasFinalVideoOnly: !images.some((img) => img.clip_url) && !!project.final_video_url,
    }),
    [images, project.edit_state, project.final_video_url],
  );

  const [state, setState] = useState<ProjectEditState | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [autosave, setAutosave] = useState<AutosaveStatus>('idle');
  const [autosaveError, setAutosaveError] = useState<string | null>(null);
  const [playing, setPlaying] = useState(false);
  const [sequenceMode, setSequenceMode] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [reworkNotes, setReworkNotes] = useState('');
  const [reworking, setReworking] = useState(false);
  const [reworkError, setReworkError] = useState<string | null>(null);
  const [trimMessage, setTrimMessage] = useState<string | null>(null);
  const [confirmRender, setConfirmRender] = useState(false);
  const [rendering, setRendering] = useState(false);
  const [reworkUsageCredits, setReworkUsageCredits] = useState<number | null>(null);
  const [renderError, setRenderError] = useState<string | null>(null);
  const [comparePending, setComparePending] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const saveTimer = useRef<number | null>(null);
  const initializedProject = useRef<string | null>(null);
  const focusedDecisionKey = useRef<string | null>(null);
  const renderKey = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    quoteImagemotionGeneration({ generated_clip_count: 1, kind: 'rework' }).then((res) => {
      if (cancelled || !res.success || res.quote?.pricing_mode !== 'usage') return;
      const credits = Number(res.quote.required_credits);
      if (Number.isFinite(credits) && credits > 0) setReworkUsageCredits(credits);
    }).catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!reconstructed.ok) {
      setState(null);
      initializedProject.current = null;
      return;
    }
    const next = mergePendingFromImages(reconstructed.state, images);
    const decision = firstClipNeedingDecision(next.clips);
    const decisionKey = decision?.pendingRework?.clipUrl
      || (decision ? `processing:${decision.id}` : null);
    if (initializedProject.current !== project.id) {
      initializedProject.current = project.id;
      focusedDecisionKey.current = decisionKey;
      setState(next);
      setSelectedId(decision?.id ?? next.clips[0]?.id ?? null);
      setComparePending(Boolean(decision?.pendingRework?.clipUrl));
      return;
    }
    setState((prev) => (prev ? mergePendingFromImages(prev, images) : next));
    if (decisionKey && focusedDecisionKey.current !== decisionKey) {
      focusedDecisionKey.current = decisionKey;
      setSelectedId(decision!.id);
      setComparePending(Boolean(decision?.pendingRework?.clipUrl));
      return;
    }
    setSelectedId((current) => current ?? next.clips[0]?.id ?? null);
  }, [reconstructed, images, project.id]);

  const selected = state?.clips.find((clip) => clip.id === selectedId) ?? state?.clips[0] ?? null;
  const selectedImage = images.find((img) => img.id === selected?.id) ?? null;
  const selectedVersion = selected ? activeVersion(selected) : null;
  const previewSrc = comparePending && selected?.pendingRework?.clipUrl
    ? selected.pendingRework.clipUrl
    : selectedVersion?.clipUrl ?? null;
  const previewDuration = comparePending && selected?.pendingRework?.duration
    ? selected.pendingRework.duration
    : selected?.sourceDuration ?? 0;

  const persist = useCallback(async (next: ProjectEditState) => {
    setAutosave('saving');
    const result = await saveProjectEdit(project.id, next as unknown as Record<string, unknown>);
    if (!result.success) {
      setAutosave('error');
      setAutosaveError(result.error || 'Could not save edit');
      return;
    }
    setAutosave('saved');
    setAutosaveError(null);
    if (result.revision) {
      setState((prev) => prev ? { ...prev, revision: result.revision! } : prev);
    }
  }, [project.id]);

  const updateState = useCallback((updater: (prev: ProjectEditState) => ProjectEditState) => {
    setState((prev) => {
      if (!prev) return prev;
      const next = updater(prev);
      if (saveTimer.current) window.clearTimeout(saveTimer.current);
      saveTimer.current = window.setTimeout(() => {
        void persist(next);
      }, 800);
      return next;
    });
  }, [persist]);

  useEffect(() => () => {
    if (saveTimer.current) window.clearTimeout(saveTimer.current);
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    if (!state) return;
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const ids = state.clips.map((clip) => clip.id);
    const oldIndex = ids.indexOf(String(active.id));
    const newIndex = ids.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    updateState((prev) => reorderClips(prev, arrayMove(ids, oldIndex, newIndex)));
  };

  const applyTrim = (start: number, end: number) => {
    if (!selected) return;
    const valid = validateTrim(start, end, selected.sourceDuration);
    if (valid.ok === false) {
      setTrimMessage(valid.error);
      return;
    }
    setTrimMessage(null);
    updateState((prev) => setClipTrim(prev, selected.id, valid.trim.start, valid.trim.end));
    const video = videoRef.current;
    if (video) video.currentTime = valid.trim.start;
  };

  const handlePlayToggle = () => {
    const video = videoRef.current;
    if (!video) return;
    if (playing) {
      video.pause();
      setPlaying(false);
      return;
    }
    if (selected && video.currentTime < selected.trimStart) {
      video.currentTime = selected.trimStart;
    }
    void video.play();
    setPlaying(true);
  };

  const handleTimeUpdate = () => {
    const video = videoRef.current;
    if (!video || !selected || !state) return;
    setCurrentTime(video.currentTime);
    if (video.currentTime >= selected.trimEnd - 0.04) {
      if (sequenceMode) {
        const playable = includedClips(state);
        const index = playable.findIndex((clip) => clip.id === selected.id);
        const nextClip = playable[index + 1];
        if (nextClip) {
          setSelectedId(nextClip.id);
          setTimeout(() => {
            const nextVideo = videoRef.current;
            if (nextVideo) {
              nextVideo.currentTime = nextClip.trimStart;
              void nextVideo.play();
            }
          }, 40);
          return;
        }
      }
      video.pause();
      video.currentTime = selected.trimStart;
      setPlaying(false);
    }
  };

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !selected) return;
    if (Math.abs(video.currentTime - selected.trimStart) > selectedDuration(selected)) {
      video.currentTime = selected.trimStart;
    }
  }, [selected?.id, previewSrc]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return;
      if (!state || !selected) return;
      if (event.code === 'Space') {
        event.preventDefault();
        handlePlayToggle();
      } else if (event.key === 'ArrowLeft' && (event.altKey || event.metaKey)) {
        updateState((prev) => moveClip(prev, selected.id, -1));
      } else if (event.key === 'ArrowRight' && (event.altKey || event.metaKey)) {
        updateState((prev) => moveClip(prev, selected.id, 1));
      } else if (event.key === 'Backspace' || event.key === 'Delete') {
        updateState((prev) => setClipIncluded(prev, selected.id, false));
      } else if (event.key.toLowerCase() === 'i') {
        updateState((prev) => setClipIncluded(prev, selected.id, true));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const startRework = async () => {
    if (!selected || !selectedImage) return;
    setReworking(true);
    setReworkError(null);
    const mapped = mapReworkInstructions(selectedImage.motion_prompt || '', reworkNotes);
    const result = await invokeGenerateMotion({
      projectId: project.id,
      projectImageId: selected.id,
      action: 'rework',
      prompt: mapped.prompt,
      instructions: reworkNotes,
    });
    if (!result.success) {
      setReworkError(result.error || 'Could not start rework');
      setReworking(false);
      return;
    }
    updateState((prev) => attachPendingRework(prev, selected.id, {
      generationId: null,
      instructions: reworkNotes,
      startedAt: new Date().toISOString(),
      status: 'processing',
    }));
    setReworking(false);
  };

  useEffect(() => {
    const pending = state?.clips.some((clip) =>
      clip.pendingRework &&
      clip.pendingRework.status !== 'completed' &&
      clip.pendingRework.status !== 'failed',
    );
    const awaitingServer = images.some((img) => img.pending_generation_id && !img.pending_clip_url);
    if (!pending && !awaitingServer) return;
    let cancelled = false;
    let inFlight = false;
    const tick = async () => {
      if (cancelled || inFlight) return;
      inFlight = true;
      try {
        await invokeGenerateMotion({ projectId: project.id, action: 'poll' });
        if (!cancelled) await onReload();
      } finally {
        inFlight = false;
      }
    };
    void tick();
    const timer = window.setInterval(() => {
      void tick();
    }, 4000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [state, images, onReload, project.id]);

  const handleAcceptRework = async (clipId: string) => {
    const image = images.find((img) => img.id === clipId);
    if (!image?.pending_clip_url || !state) return;
    setSelectedId(clipId);
    await acceptPendingClip(image.id, image);
    const accepted = acceptRework(state, clipId);
    setTrimMessage(accepted.trimMessage ?? null);
    setComparePending(false);
    updateState(() => accepted.state);
    await onReload();
  };

  const handleKeepOriginal = async (clipId: string) => {
    const image = images.find((img) => img.id === clipId);
    if (!image) return;
    setSelectedId(clipId);
    await discardPendingClip(image.id);
    updateState((prev) => rejectRework(prev, clipId));
    setComparePending(false);
  };

  const handleRender = async () => {
    if (!state) return;
    const valid = validateEditState(state);
    if (valid.ok === false) {
      setRenderError(valid.error);
      return;
    }
    setRendering(true);
    setRenderError(null);
    await persist(state);
    if (!renderKey.current) renderKey.current = crypto.randomUUID();
    const result = await invokeRender(project.id, {
      idempotencyKey: renderKey.current,
      editState: state as unknown as Record<string, unknown>,
    });
    if (!result.success) {
      setRendering(false);
      setConfirmRender(false);
      setRenderError(result.error || 'Render failed');
      renderKey.current = null;
      return;
    }
    setConfirmRender(false);
    setRendering(false);
    renderKey.current = null;
    await onReload();
  };

  if (!reconstructed.ok) {
    return (
      <div className="bg-slate-900 border border-slate-800 rounded-lg p-6">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
          <div>
            <h2 className="text-lg font-semibold text-white">Review & edit unavailable</h2>
            <p className="text-sm text-slate-400 mt-1">{reconstructed.ok === false ? reconstructed.message : ''}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!state || !selected) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-blue-400" />
      </div>
    );
  }

  const total = totalEditedDuration(state);
  const localRework = quoteImagemotionUsage({ generatedClipCount: 1, kind: 'rework' });
  const credits = reworkCreditCopy(
    (project.credits_charged ?? 0) > 0,
    project.credits_charged || 0,
    reworkUsageCredits ?? (localRework.chargeable ? localRework.requiredCredits : null),
  );
  const renderCredits = renderCreditCopy();
  const approximate = previewNeedsApproximation(project.settings || {}, !!project.music_url, !!project.logo_url);
  const canRender = validateEditState(state).ok && !rendering && project.status !== 'rendering';
  const dirty = (project.last_rendered_edit_revision ?? null) !== state.revision && !!project.final_video_url;
  const deciding = Boolean(selected.pendingRework?.clipUrl);

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-lg p-5 sm:p-6 space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-white">Review & edit</h2>
          <p className="text-sm text-slate-400">Adjust clips, then render the finished video.</p>
        </div>
        <div className="flex items-center gap-3 text-xs text-slate-400">
          <span className="inline-flex items-center gap-1">
            <Clock className="w-3.5 h-3.5" />
            {formatTimecode(total)}
          </span>
          <span>
            {autosave === 'saving' && 'Saving…'}
            {autosave === 'saved' && 'Saved'}
            {autosave === 'error' && (autosaveError || 'Save failed')}
            {autosave === 'idle' && 'Draft'}
          </span>
          {dirty && <span className="text-amber-400">Draft differs from last export</span>}
        </div>
      </div>

      {approximate && (
        <p className="text-xs text-slate-500 bg-slate-950/70 border border-slate-800 rounded-md px-3 py-2">
          Browser preview is approximate. Transitions, music and overlays are added in the final render.
        </p>
      )}

      {renderError && (
        <div className="text-sm text-red-300 bg-red-950/30 border border-red-900/40 rounded-md px-3 py-2">
          {renderError}
        </div>
      )}

      {!renderError && project.status !== 'rendering' && project.error_message && (
        <div className="text-sm text-red-300 bg-red-950/30 border border-red-900/40 rounded-md px-3 py-2">
          {project.error_message}
        </div>
      )}

      <div className={`rounded-lg overflow-hidden bg-black ${project.settings?.aspect_ratio === '9:16' ? 'max-w-sm mx-auto' : ''}`}>
        {previewSrc ? (
          <video
            ref={videoRef}
            key={`${selected.id}-${comparePending ? 'pending' : selected.activeVersionId}`}
            src={previewSrc}
            className="w-full bg-black"
            playsInline
            onTimeUpdate={handleTimeUpdate}
            onPlay={() => setPlaying(true)}
            onPause={() => setPlaying(false)}
          />
        ) : (
          <div className="aspect-video flex items-center justify-center text-slate-500 text-sm">Select a clip to preview</div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={handlePlayToggle}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-800 text-white text-sm hover:bg-slate-700"
        >
          {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          {playing ? 'Pause' : 'Play'}
        </button>
        <button
          type="button"
          onClick={() => setSequenceMode((value) => !value)}
          className={`px-3 py-2 rounded-lg text-sm ${sequenceMode ? 'bg-blue-600 text-white' : 'bg-slate-800 text-slate-300'}`}
        >
          {sequenceMode ? 'Playing edited sequence' : 'Playing selected clip'}
        </button>
        <input
          type="range"
          min={selected.trimStart}
          max={selected.trimEnd}
          step={0.04}
          value={Math.min(Math.max(currentTime, selected.trimStart), selected.trimEnd)}
          onChange={(event) => {
            const next = Number(event.target.value);
            setCurrentTime(next);
            if (videoRef.current) videoRef.current.currentTime = next;
          }}
          className="flex-1 min-w-[160px]"
          aria-label="Scrub clip"
        />
        <span className="text-xs text-slate-400 tabular-nums">
          {formatTimecode(currentTime)} / {formatTimecode(selectedDuration(selected))}
        </span>
      </div>

      <div>
        <div className="flex items-center gap-2 mb-2">
          <Film className="w-4 h-4 text-slate-500" />
          <h3 className="text-sm font-medium text-slate-300">Timeline</h3>
        </div>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={state.clips.map((clip) => clip.id)} strategy={horizontalListSortingStrategy}>
            <div className="flex gap-3 overflow-x-auto pb-2 items-start">
              {state.clips.map((clip, index) => {
                const hasNew = Boolean(clip.pendingRework?.clipUrl);
                const waiting = Boolean(clip.pendingRework) && !hasNew && clip.pendingRework?.status !== 'failed';
                const paired = hasNew || waiting;
                return (
                  <div
                    key={clip.id}
                    className={paired ? 'shrink-0 flex flex-col gap-2 rounded-xl border border-indigo-500/30 bg-indigo-950/20 p-1.5' : ''}
                  >
                    <div className="flex gap-2">
                      <SortableClipCard
                        clip={clip}
                        index={index}
                        paired={paired}
                        selected={clip.id === selected.id && !comparePending}
                        onSelect={() => {
                          setSelectedId(clip.id);
                          setComparePending(false);
                          setReworkNotes(clip.pendingRework?.instructions || '');
                        }}
                      />
                      {paired && (
                        <ChoiceClipCard
                          clip={clip}
                          index={index}
                          selected={clip.id === selected.id && comparePending}
                          onSelect={() => {
                            setSelectedId(clip.id);
                            setComparePending(true);
                            setReworkNotes(clip.pendingRework?.instructions || '');
                          }}
                        />
                      )}
                    </div>
                    {hasNew && (
                      <div className="flex flex-col gap-1.5 px-0.5 pb-0.5 w-[18.5rem]">
                        <p className="text-[11px] leading-snug text-indigo-100/90">
                          Choose which version of Clip {index + 1} stays in the video.
                        </p>
                        <button
                          type="button"
                          onClick={() => void handleAcceptRework(clip.id)}
                          className="inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm rounded-lg bg-green-700 hover:bg-green-600 text-white"
                        >
                          <Check className="w-4 h-4" /> Use this new version
                        </button>
                        <button
                          type="button"
                          onClick={() => void handleKeepOriginal(clip.id)}
                          className="inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200"
                        >
                          Keep this original
                        </button>
                      </div>
                    )}
                    {waiting && (
                      <p className="text-[11px] leading-snug text-indigo-200 px-0.5 pb-0.5 w-[18.5rem]">
                        New take is generating. Choose once it appears beside the original.
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </SortableContext>
        </DndContext>
        <div className="flex flex-wrap gap-2 mt-3">
          <button type="button" onClick={() => updateState((prev) => moveClip(prev, selected.id, -1))} className="inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded-md bg-slate-800 text-slate-200 hover:bg-slate-700">
            <ChevronLeft className="w-3.5 h-3.5" /> Move left
          </button>
          <button type="button" onClick={() => updateState((prev) => moveClip(prev, selected.id, 1))} className="inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded-md bg-slate-800 text-slate-200 hover:bg-slate-700">
            Move right <ChevronRight className="w-3.5 h-3.5" />
          </button>
          {selected.included ? (
            <button type="button" onClick={() => updateState((prev) => setClipIncluded(prev, selected.id, false))} className="inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded-md bg-slate-800 text-amber-300 hover:bg-slate-700">
              <Trash2 className="w-3.5 h-3.5" /> Exclude from video
            </button>
          ) : (
            <button type="button" onClick={() => updateState((prev) => setClipIncluded(prev, selected.id, true))} className="inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded-md bg-green-700 text-white hover:bg-green-600">
              <Undo2 className="w-3.5 h-3.5" /> Include again
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 border-t border-slate-800 pt-5">
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Scissors className="w-4 h-4 text-slate-500" />
            <h3 className="text-sm font-medium text-slate-300">Trim clip</h3>
          </div>
          <div className="relative h-8 rounded bg-slate-950 border border-slate-800">
            <div
              className="absolute inset-y-0 bg-blue-500/30"
              style={{
                left: `${(selected.trimStart / Math.max(previewDuration, 0.01)) * 100}%`,
                width: `${(selectedDuration(selected) / Math.max(previewDuration, 0.01)) * 100}%`,
              }}
            />
            <input
              type="range"
              min={0}
              max={previewDuration}
              step={0.04}
              value={selected.trimStart}
              onChange={(event) => applyTrim(Number(event.target.value), selected.trimEnd)}
              className="absolute inset-0 w-full opacity-60"
              aria-label="Trim start"
            />
            <input
              type="range"
              min={0}
              max={previewDuration}
              step={0.04}
              value={selected.trimEnd}
              onChange={(event) => applyTrim(selected.trimStart, Number(event.target.value))}
              className="absolute inset-0 w-full opacity-60"
              aria-label="Trim end"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="text-xs text-slate-400">
              Start
              <input
                type="text"
                defaultValue={selected.trimStart.toFixed(2)}
                key={`start-${selected.id}-${selected.trimStart}`}
                onBlur={(event) => {
                  const parsed = parseTimeInput(event.target.value);
                  if (parsed != null) applyTrim(parsed, selected.trimEnd);
                }}
                className="mt-1 w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-white text-sm"
              />
            </label>
            <label className="text-xs text-slate-400">
              End
              <input
                type="text"
                defaultValue={selected.trimEnd.toFixed(2)}
                key={`end-${selected.id}-${selected.trimEnd}`}
                onBlur={(event) => {
                  const parsed = parseTimeInput(event.target.value);
                  if (parsed != null) applyTrim(selected.trimStart, parsed);
                }}
                className="mt-1 w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-white text-sm"
              />
            </label>
          </div>
          <p className="text-xs text-slate-500">
            Original {formatTimecode(selected.sourceDuration)} · Selected {formatTimecode(selectedDuration(selected))}
          </p>
          {deciding && (
            <p className="text-xs text-slate-500">
              Trim applies after you choose which version stays in the video.
            </p>
          )}
          {trimMessage && <p className="text-xs text-amber-300">{trimMessage}</p>}
          <button
            type="button"
            onClick={() => updateState((prev) => resetClipTrim(prev, selected.id))}
            className="inline-flex items-center gap-1 text-xs text-slate-300 hover:text-white"
          >
            <RotateCcw className="w-3.5 h-3.5" /> Reset trim
          </button>
        </div>

        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-slate-500" />
            <h3 className="text-sm font-medium text-slate-300">Rework clip</h3>
          </div>
          {project.service_type === 'hyper_dusk' ? (
            <p className="text-xs text-slate-500">
              Hyper Dusk clips are generated in the dusk workflow. Use trim, exclude and reorder here, then render the finished video.
            </p>
          ) : (
            <>
          <p className="text-xs text-slate-500">
            Describe the change you want. We map this onto camera-motion controls — results are not guaranteed.
          </p>
          <div className="flex flex-wrap gap-1.5">
            {REWORK_EXAMPLES.map((example) => (
              <button
                key={example}
                type="button"
                onClick={() => setReworkNotes(example)}
                className="px-2 py-1 text-[11px] rounded-full bg-slate-800 text-slate-300 hover:bg-slate-700"
              >
                {example}
              </button>
            ))}
          </div>
          <textarea
            value={reworkNotes}
            onChange={(event) => setReworkNotes(event.target.value)}
            rows={3}
            maxLength={500}
            placeholder="e.g. Slower camera movement"
            className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm text-white"
          />
          <p className="text-xs text-slate-400">{credits.label}</p>
          {reworkError && <p className="text-xs text-red-300">{reworkError}</p>}
          <button
            type="button"
            disabled={reworking || !reworkNotes.trim()}
            onClick={() => void startRework()}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-indigo-600 text-white text-sm hover:bg-indigo-500 disabled:opacity-50"
          >
            {reworking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {reworking ? 'Starting…' : 'Rework this clip'}
          </button>
          {credits.credits > 0 && onOpenBuyCredits && (
            <button type="button" onClick={onOpenBuyCredits} className="block text-xs text-blue-400 hover:underline">
              Need more credits?
            </button>
          )}
            </>
          )}

          {selected.pendingRework?.status === 'processing' && (
            <p className="text-xs text-indigo-300">Generating a replacement. The current clip stays usable.</p>
          )}
          {selected.pendingRework?.clipUrl && (
            <p className="text-xs text-indigo-200">
              Choose under this clip in the timeline: use the new version, or keep the original.
            </p>
          )}

          {selected.versions.length > 1 && (
            <div className="space-y-1">
              <p className="text-xs text-slate-500">Version history</p>
              {selected.versions.map((version, index) => (
                <button
                  key={version.id}
                  type="button"
                  onClick={() => {
                    const restored = restoreVersion(state, selected.id, version.id);
                    setTrimMessage(restored.trimMessage ?? null);
                    updateState(() => restored.state);
                  }}
                  className={`block w-full text-left px-2 py-1.5 rounded text-xs ${
                    version.id === selected.activeVersionId ? 'bg-slate-800 text-blue-300' : 'text-slate-400 hover:bg-slate-800'
                  }`}
                >
                  Version {selected.versions.length - index}
                  {version.instructions ? ` · ${version.instructions}` : ''}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-t border-slate-800 pt-5">
        <p className="text-sm text-slate-400">
          {includedClips(state).length} clip{includedClips(state).length === 1 ? '' : 's'} · {formatTimecode(total)}
        </p>
        <button
          type="button"
          disabled={!canRender}
          onClick={() => setConfirmRender(true)}
          className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-500 disabled:opacity-50"
        >
          {project.status === 'rendering' || rendering ? <Loader2 className="w-4 h-4 animate-spin" /> : <Film className="w-4 h-4" />}
          {project.status === 'rendering' || rendering ? 'Rendering…' : 'Render final video'}
        </button>
      </div>

      {confirmRender && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="w-full max-w-md rounded-xl bg-slate-900 border border-slate-700 p-5 space-y-4">
            <h3 className="text-lg font-semibold text-white">Render final video?</h3>
            <p className="text-sm text-slate-400">
              We’ll compose the included clips in their current order, with your trims, plus this project’s branding, captions, music and transitions.
            </p>
            <p className="text-sm text-slate-300">{renderCredits.label}</p>
            <p className="text-xs text-slate-500">Edited duration: {formatTimecode(total)}</p>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setConfirmRender(false)} className="px-3 py-2 text-sm rounded-lg bg-slate-800 text-slate-200">
                Cancel
              </button>
              <button
                type="button"
                disabled={rendering}
                onClick={() => void handleRender()}
                className="px-3 py-2 text-sm rounded-lg bg-blue-600 text-white"
              >
                {rendering ? 'Starting…' : 'Render now'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
